import type { KeyboardEvent, ReactNode } from 'react';
import { eventSentence, type EventLogRow } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { SkeletonLines } from '../../../../components/Skeleton';
import { formatClockTime, formatZoneName } from '../../../../lib/format';
import { groupByDay } from '../access-log/access-log-words';
import { actorLabelOf, nextTurnOf, recordLineOf, recordNameOf } from './event-log-words';
import styles from './EventLogTable.module.css';

// Event log A's table: Time · What happened (the plain sentence over the technical name) · Record (name, then kind ·
// job · venture) · Who, with whose turn it became under the name — only when the event moved the work. Rows sit under
// one heading per Dhaka day. ↑ ↓ move between rows, each one opening in the pane beside it.

/** Marks a row the arrow keys move between. */
const ROW_ATTRIBUTE = 'data-event-row';

interface EventLogTableProps {
  readonly rows: readonly EventLogRow[];
  readonly selectedId: string | null;
  readonly onSelect: (row: EventLogRow) => void;
  /** Beside the pane, the arrow keys open each row they reach; where the pane is its own view they only move. */
  readonly isSelectOnMove: boolean;
  readonly isFirstLoad: boolean;
  /** Shown in place of the rows: an empty or failed state. */
  readonly emptyState: ReactNode;
}

export function EventLogTable({ rows, selectedId, onSelect, isSelectOnMove, isFirstLoad, emptyState }: EventLogTableProps): ReactNode {
  const groups = groupByDay(rows);
  const zone = formatZoneName(rows[0]?.occurredAt ?? new Date());

  const move = (event: KeyboardEvent<HTMLElement>): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.hasAttribute(ROW_ATTRIBUTE)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(`[${ROW_ATTRIBUTE}]`));
    const index = items.indexOf(target);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = Math.min(items.length - 1, index + 1);
    else if (event.key === 'ArrowUp') next = Math.max(0, index - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    if (next === null || next === index) return;
    event.preventDefault();
    items[next]?.focus();
    if (!isSelectOnMove) return;
    const id = items[next]?.getAttribute(ROW_ATTRIBUTE);
    const row = rows.find((candidate) => candidate.id === id);
    if (row) onSelect(row);
  };

  return (
    <section className={styles.table} aria-label="Events">
      <div className={styles.head} aria-hidden>
        <span>Time</span>
        <span>What happened</span>
        <span>Record</span>
        <span>Who, and whose turn next</span>
      </div>

      {isFirstLoad ? (
        <div className={styles.skeleton}>
          <SkeletonLines count={10} />
        </div>
      ) : rows.length === 0 ? (
        emptyState
      ) : (
        <div onKeyDown={move}>
          {groups.map((group, index) => (
            <section key={group.day} aria-label={group.label}>
              <h3 className={styles.day}>
                {group.label.replace(' · ', ', ')}
                {index === 0 && <span className={styles.zone}> · times in {zone}</span>}
              </h3>
              <ul className={styles.rows}>
                {group.entries.map((row) => (
                  <li key={row.id}>
                    <Row row={row} isSelected={row.id === selectedId} onSelect={onSelect} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

function Row({ row, isSelected, onSelect }: { readonly row: EventLogRow; readonly isSelected: boolean; readonly onSelect: (row: EventLogRow) => void }): ReactNode {
  const isSystem = row.actorType === 'system';
  const who = actorLabelOf(row);
  const next = nextTurnOf(row);
  return (
    <button type="button" className={styles.row} aria-current={isSelected || undefined} onClick={() => onSelect(row)} {...{ [ROW_ATTRIBUTE]: row.id }}>
      <span className={styles.time}>{formatClockTime(row.occurredAt)}</span>
      <span className={styles.what}>
        <span className={styles.sentence}>{eventSentence(row)}</span>
        <code className={styles.code}>{row.eventType}</code>
      </span>
      <span className={styles.record}>
        <span className={styles.recordName} data-anonymised={row.record?.isAnonymised || undefined}>
          {recordNameOf(row)}
        </span>
        <span className={styles.recordLine}>{recordLineOf(row)}</span>
      </span>
      <span className={styles.who}>
        <Avatar name={who} size="sm" isSystem={isSystem} />
        <span className={styles.whoText}>
          <span className={styles.whoName} data-system={isSystem || undefined}>
            {who}
          </span>
          {next && <span className={styles.next}>Next: {next}</span>}
        </span>
      </span>
    </button>
  );
}
